/**
 * BidNotesBrief
 * -------------
 * Detail view for a notes-based opportunity: the engineering brief (every fact
 * tagged with its source), the deterministic calculation sheet (editable inputs
 * → recalculated in code, never by the model), the option matrix, the draft
 * recommendations flagged for engineer review, the clarifying questions, the
 * draft technical response letter and the BOM priced off the price book.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { openAuthedDoc } from "@/lib/openAuthedDoc";
import {
  Loader2, RefreshCw, Download, Copy, FileText, Paperclip, Plus, Save,
  AlertTriangle, CheckCircle2, Calculator, HelpCircle, ClipboardList, BookOpen,
  ChevronLeft, NotebookPen,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { API } from "@/App";
import EquipmentPriceBook from "@/components/admin/EquipmentPriceBook";
import BidNotesScope from "@/components/admin/BidNotesScope";
import BidNotesProposal from "@/components/admin/BidNotesProposal";
import BidOwnerDecisions from "@/components/admin/BidOwnerDecisions";
import BidScopeTemplates from "@/components/admin/BidScopeTemplates";
import StandardsCompass from "@/components/admin/StandardsCompass";
import SiteSurveyPanel from "@/components/admin/SiteSurveyPanel";
import BidAwardPanel from "@/components/admin/BidAwardPanel";

const BE = process.env.REACT_APP_BACKEND_URL;

const Src = ({ children }) => children ? (
  <span className="inline-block text-[10px] text-slate-500 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5 ml-1 align-middle">
    {children}
  </span>
) : null;

const Section = ({ icon: Icon, title, hint, right, children, testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={testid}>
    <div className="flex items-start justify-between gap-3 mb-3">
      <div>
        <h3 className="text-sm font-black uppercase tracking-wider text-slate-700 inline-flex items-center gap-1.5">
          {Icon && <Icon className="w-4 h-4 text-slate-400" />} {title}
        </h3>
        {hint && <p className="text-[11px] text-slate-500 mt-0.5">{hint}</p>}
      </div>
      {right}
    </div>
    {children}
  </div>
);

const CALC_FIELDS = [
  ["units", "Units"],
  ["cycles_per_day", "Cycles / day"],
  ["gal_per_cycle", "Gal / cycle (treated)"],
  ["idle_gph_per_unit", "Idle GPH / unit"],
  ["operating_hours", "Operating hours"],
  ["peak_gpm_per_unit", "Peak GPM / unit"],
  ["safety_factor_pct", "Safety factor %"],
  ["ro_train_gpd", "RO train GPD"],
];

export default function BidNotesBrief({ note, token, onBack, onRefresh }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [doc, setDoc] = useState(note);
  const [inputs, setInputs] = useState(note?.notes_brief?.calc_inputs || {});
  const [letter, setLetter] = useState(note?.response_letter || "");
  const [bom, setBom] = useState(note?.bom || []);
  const [priceRows, setPriceRows] = useState([]);
  const [showBook, setShowBook] = useState(false);
  const [busy, setBusy] = useState(false);
  const [followUp, setFollowUp] = useState("");

  const reload = useCallback(async () => {
    const r = await axios.get(`${API}/bid-desk/notes/${note.id}`, { headers });
    const n = r.data.note;
    setDoc(n);
    setInputs(n?.notes_brief?.calc_inputs || {});
    setLetter(n?.response_letter || "");
    setBom(n?.bom || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [note.id, token]);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    axios.get(`${API}/bid-desk/price-book`, { headers })
      .then((r) => setPriceRows(r.data?.rows || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, showBook]);

  // poll while AskIsaac is reading
  useEffect(() => {
    if (doc?.parse_status !== "parsing") return;
    const t = setInterval(reload, 6000);
    return () => clearInterval(t);
  }, [doc?.parse_status, reload]);

  const brief = doc?.notes_brief || {};
  const calc = doc?.calc_sheet || {};
  const proj = brief.project || {};

  const recalc = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/bid-desk/notes/${note.id}/recalc`,
        { calc_inputs: inputs }, { headers });
      setDoc((d) => ({ ...d, calc_sheet: r.data.calc_sheet }));
      toast.success("Load math recalculated from your inputs");
    } catch { toast.error("Could not recalculate"); } finally { setBusy(false); }
  };

  const reanalyze = async () => {
    try {
      await axios.post(`${API}/bid-desk/notes/${note.id}/reanalyze`, {}, { headers });
      setDoc((d) => ({ ...d, parse_status: "parsing" }));
      toast.info("Re-reading the notes and every attachment…");
    } catch { toast.error("Could not re-analyse"); }
  };

  const saveAll = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/bid-desk/notes/${note.id}`, {
        response_letter: letter, bom, notes_brief: { ...brief, calc_inputs: inputs },
      }, { headers });
      toast.success("Saved");
      onRefresh?.();
      reload();
    } catch { toast.error("Save failed"); } finally { setBusy(false); }
  };

  const copyQuestions = async () => {
    try {
      const r = await axios.get(`${API}/bid-desk/notes/${note.id}/questions`, { headers });
      await navigator.clipboard.writeText(
        `To: ${r.data.to}\nSubject: ${r.data.subject}\n\n${r.data.body}`);
      toast.success(`${r.data.count} questions copied`, { description: "Paste into Outlook and send" });
    } catch { toast.error("Could not build the questions email"); }
  };

  const copyLetter = async () => {
    const tmp = document.createElement("div");
    tmp.innerHTML = letter;
    await navigator.clipboard.writeText(tmp.innerText);
    toast.success("Response letter copied as text");
  };

  const addFollowUp = async () => {
    if (!followUp.trim()) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("notes", followUp);
      fd.append("label", "Follow-up");
      await axios.post(`${API}/bid-desk/notes/${note.id}/append`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" } });
      setFollowUp("");
      setDoc((d) => ({ ...d, parse_status: "parsing" }));
      toast.success("Added to the thread — re-reading everything now");
    } catch { toast.error("Could not append"); } finally { setBusy(false); }
  };

  const bomTotal = bom.reduce((s, b) => s + (Number(b.qty) || 0) * (Number(b.unit_price) || 0), 0);
  const markup = Number(doc?.markup_pct || 0);
  const sell = bomTotal * (1 + markup / 100);

  const priceFromBook = (i, rowId) => {
    const pr = priceRows.find((p) => p.id === rowId);
    if (!pr) return;
    setBom((b) => b.map((x, idx) => (idx === i
      ? { ...x, price_book_id: rowId, unit_price: pr.unit_price, uom: pr.uom || x.uom,
          description: x.description || pr.description }
      : x)));
  };

  return (
    <div className="space-y-4 overflow-x-hidden" data-testid="bid-notes-brief">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-2">
          <Button size="sm" variant="ghost" onClick={onBack} data-testid="notes-back-btn">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div>
            <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-2 py-0.5">
              <NotebookPen className="w-3 h-3" /> Notes &amp; specs
            </span>
            <h1 className="text-xl font-black text-slate-900 mt-1">
              {doc?.title || proj.name || "Notes-based opportunity"}
            </h1>
            <p className="text-sm text-slate-500">
              {doc?.customer || proj.facility || "—"}
              {proj.requester?.name ? ` · ${proj.requester.name}` : ""}
              {proj.requester?.company ? ` (${proj.requester.company})` : ""}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setShowBook(true)} data-testid="notes-price-book-btn">
            <BookOpen className="w-3.5 h-3.5 mr-1" /> Price book
          </Button>
          <Button size="sm" variant="outline" onClick={reanalyze} data-testid="notes-reanalyze-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Re-read
          </Button>
          <Button size="sm" onClick={saveAll} disabled={busy} className="bg-slate-900 hover:bg-slate-800"
            data-testid="notes-save-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save
          </Button>
        </div>
      </div>

      {doc?.parse_status === "parsing" && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          AskIsaac is reading the notes with {(doc.attachments || []).length} attachment(s) — about a minute.
        </div>
      )}
      {doc?.parse_status === "failed" && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertTriangle className="w-4 h-4 inline mr-1" /> {doc.parse_error}
        </div>
      )}

      {brief.executive_summary && (
        <Section icon={ClipboardList} title="Executive summary" testid="notes-summary">
          <p className="text-sm text-slate-700 leading-relaxed">{brief.executive_summary}</p>
          {!!(doc.attachments || []).length && (
            <div className="flex flex-wrap gap-2 mt-3">
              {doc.attachments.map((a) => (
                <a key={a.id} href={`${BE}${a.url}`} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-[11px] text-slate-600 border border-slate-200 rounded px-2 py-1 hover:bg-slate-50">
                  <Paperclip className="w-3 h-3" /> {a.name}
                </a>
              ))}
            </div>
          )}
        </Section>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <Section icon={CheckCircle2} title="Stated requirements"
          hint="Each fact carries the document it came from" testid="notes-requirements">
          <ul className="space-y-1.5">
            {(brief.requirements || []).map((r, i) => (
              <li key={i} className="text-sm text-slate-700">
                <strong>{r.item}:</strong> {r.value} <Src>{r.source}</Src>
              </li>
            ))}
            {!(brief.requirements || []).length && <li className="text-sm text-slate-400">—</li>}
          </ul>
        </Section>

        <Section icon={AlertTriangle} title="Compliance limits found"
          hint="Pulled off the cutsheets and general notes" testid="notes-limits">
          <ul className="space-y-1.5">
            {(brief.compliance_limits || []).map((c, i) => (
              <li key={i} className="text-sm text-slate-700">
                <strong>{c.parameter}:</strong> {c.limit}
                {c.standard ? <em className="text-slate-500"> · {c.standard}</em> : null}
                <Src>{c.source}</Src>
              </li>
            ))}
            {!(brief.compliance_limits || []).length && <li className="text-sm text-slate-400">—</li>}
          </ul>
        </Section>

        <Section icon={FileText} title="Equipment identified" testid="notes-equipment">
          <ul className="space-y-1.5">
            {(brief.equipment_identified || []).map((e, i) => (
              <li key={i} className="text-sm text-slate-700">
                <strong>{[e.manufacturer, e.model].filter(Boolean).join(" ")}</strong>
                {e.qty ? ` × ${e.qty}` : ""} {e.drawing_no ? `· Dwg ${e.drawing_no}` : ""}
                {e.detail ? <span className="block text-slate-500 text-xs">{e.detail}</span> : null}
                <Src>{e.source}</Src>
              </li>
            ))}
            {!(brief.equipment_identified || []).length && <li className="text-sm text-slate-400">—</li>}
          </ul>
        </Section>

        <Section icon={ClipboardList} title="Site constraints" testid="notes-constraints">
          <ul className="space-y-1.5">
            {(brief.site_constraints || []).map((s, i) => (
              <li key={i} className="text-sm text-slate-700">
                <strong>{s.item}:</strong> {s.detail} <Src>{s.source}</Src>
              </li>
            ))}
            {!(brief.site_constraints || []).length && <li className="text-sm text-slate-400">—</li>}
          </ul>
        </Section>
      </div>

      <Section icon={Calculator} title="Calculation sheet"
        hint="AskIsaac extracts the inputs; the arithmetic is done in code so every number is defensible"
        right={<Button size="sm" variant="outline" onClick={recalc} disabled={busy} data-testid="notes-recalc-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Calculator className="w-3.5 h-3.5 mr-1" />} Recalculate
        </Button>}
        testid="notes-calc">
        <div className="grid sm:grid-cols-4 gap-2 mb-3">
          {CALC_FIELDS.map(([k, label]) => (
            <div key={k}>
              <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">{label}</label>
              <Input type="number" value={inputs[k] ?? ""} className="h-8 text-xs"
                onChange={(e) => setInputs((v) => ({ ...v, [k]: e.target.value }))}
                data-testid={`calc-input-${k}`} />
            </div>
          ))}
        </div>
        {!!(calc.warnings || []).length && (
          <div className="rounded border border-amber-200 bg-amber-50 p-2 mb-3 text-[12px] text-amber-800">
            {calc.warnings.map((w, i) => <p key={i}><AlertTriangle className="w-3 h-3 inline mr-1" />{w}</p>)}
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-slate-50">
              <tr>
                {["Step", "Formula", "Result"].map((h) => (
                  <th key={h} className="text-left px-2 py-1.5 text-[10px] font-black uppercase tracking-wider text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(calc.steps || []).map((s, i) => (
                <tr key={i}>
                  <td className="px-2 py-1.5 text-slate-700">{s.label}</td>
                  <td className="px-2 py-1.5 font-mono text-[11px] text-slate-500">{s.formula}</td>
                  <td className="px-2 py-1.5 text-right font-bold text-slate-900 whitespace-nowrap">{s.result}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!!(calc.excluded_loads || []).length && (
          <div className="mt-3 rounded border border-sky-200 bg-sky-50 p-2.5">
            <p className="text-[10px] font-black uppercase tracking-wider text-sky-800 mb-1">Excluded from the RO load</p>
            {calc.excluded_loads.map((x, i) => (
              <p key={i} className="text-[12px] text-sky-900">
                <strong>{x.label}</strong> — {x.gpm} GPM/unit ({x.aggregate_gpm} GPM aggregate): {x.reason}
              </p>
            ))}
          </div>
        )}
      </Section>

      {!!(brief.options || []).length && (
        <Section icon={ClipboardList} title="System selection options" testid="notes-options">
          <div className="grid md:grid-cols-2 gap-3">
            {brief.options.map((o, i) => (
              <div key={i} className="rounded-lg border border-slate-200 p-3">
                <p className="text-sm font-black text-slate-900">{o.name}</p>
                <p className="text-xs text-slate-500">{[o.capacity, o.redundancy].filter(Boolean).join(" · ")}</p>
                {o.duty && <p className="text-[12px] text-slate-600 mt-1">{o.duty}</p>}
                <ul className="mt-2 space-y-0.5">
                  {(o.components || []).map((c, j) => (
                    <li key={j} className="text-[12px] text-slate-600">
                      • {c.description} {c.qty ? `(${c.qty} ${c.uom || ""})` : ""}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Section>
      )}

      {!!(brief.recommendations || []).length && (
        <Section icon={AlertTriangle} title="Engineering recommendations"
          hint="Draft — engineer review required before release" testid="notes-recs">
          <div className="space-y-2.5">
            {brief.recommendations.map((r, i) => (
              <div key={i} className="rounded-lg border border-amber-200 bg-amber-50/60 p-3">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-black text-slate-900">{r.title}</p>
                  <span className="text-[9px] font-black uppercase tracking-wider text-amber-800 bg-white border border-amber-300 rounded px-1.5 py-0.5 shrink-0">
                    draft · {r.confidence || "review"}
                  </span>
                </div>
                <p className="text-[13px] text-slate-800 mt-1">{r.recommendation}</p>
                {r.rationale && <p className="text-[12px] text-slate-600 mt-1">{r.rationale}</p>}
                <p className="text-[11px] text-slate-500 mt-1">
                  {(r.standards || []).join(" · ")} <Src>{r.source}</Src>
                </p>
              </div>
            ))}
          </div>
        </Section>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <Section icon={HelpCircle} title="Questions for the customer"
          right={<Button size="sm" variant="outline" onClick={copyQuestions} data-testid="notes-copy-questions">
            <Copy className="w-3.5 h-3.5 mr-1" /> Copy email
          </Button>} testid="notes-questions">
          <ol className="list-decimal pl-5 space-y-1.5">
            {(doc.open_questions || []).map((q, i) => (
              <li key={i} className="text-sm text-slate-700">
                {q.question}
                {q.why && <span className="block text-[11px] text-slate-500">{q.why}</span>}
              </li>
            ))}
            {!(doc.open_questions || []).length && <li className="text-sm text-slate-400">—</li>}
          </ol>
        </Section>

        <Section icon={AlertTriangle} title="Internal unknowns" hint="For our engineer, not the customer"
          testid="notes-unknowns">
          <ul className="space-y-1.5">
            {(brief.internal_unknowns || []).map((u, i) => (
              <li key={i} className="text-sm text-slate-700">
                <strong>{u.item}</strong>
                {u.assumption && <span className="block text-[12px] text-slate-600">Assumed: {u.assumption}</span>}
                {u.needs && <span className="block text-[11px] text-slate-500">Needs: {u.needs}</span>}
              </li>
            ))}
            {!(brief.internal_unknowns || []).length && <li className="text-sm text-slate-400">—</li>}
          </ul>
        </Section>
      </div>

      <Section icon={FileText} title="Draft technical response"
        hint="Grounded in the brief and the calculation sheet — edit before it goes out"
        right={
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={copyLetter} data-testid="notes-copy-letter">
              <Copy className="w-3.5 h-3.5 mr-1" /> Copy
            </Button>
            <button onClick={() => openAuthedDoc(`/bid-desk/notes/${note.id}/letter`, { token, fmt: "html" })}
              className="inline-flex items-center h-8 px-2.5 rounded-md border border-slate-300 text-xs hover:bg-slate-50">
              <FileText className="w-3.5 h-3.5 mr-1" /> Preview
            </button>
            <button onClick={() => openAuthedDoc(`/bid-desk/notes/${note.id}/letter`, { token, fmt: "pdf" })}
              className="inline-flex items-center h-8 px-2.5 rounded-md border border-slate-300 text-xs hover:bg-slate-50">
              <Download className="w-3.5 h-3.5 mr-1" /> PDF
            </button>
          </div>
        } testid="notes-letter">
        <div className="prose prose-sm max-w-none border border-slate-200 rounded-lg p-3 bg-slate-50/60 max-h-80 overflow-auto"
          dangerouslySetInnerHTML={{ __html: letter || "<p class='text-slate-400'>No draft yet.</p>" }} />
        <Textarea value={letter} onChange={(e) => setLetter(e.target.value)} rows={8}
          className="mt-2 font-mono text-[11px]" data-testid="notes-letter-html" />
      </Section>

      <BidNotesScope noteId={note.id} token={token}
        initialScope={doc?.scope_of_work} initialStatus={doc?.scope_status} />

      <Section icon={ClipboardList} title="Quote BOM"
        hint="Priced from the equipment price book — fill the book in once and every quote reuses it"
        right={
          <Button size="sm" variant="outline" data-testid="notes-bom-add"
            onClick={() => setBom((b) => [...b, { description: "", qty: 1, uom: "Each", unit_price: 0 }])}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Line
          </Button>
        } testid="notes-bom">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead className="bg-slate-50">
              <tr>
                {["Description", "Price book", "Qty", "UOM", "Unit $", "Ext $"].map((h) => (
                  <th key={h} className="text-left px-2 py-1.5 text-[10px] font-black uppercase tracking-wider text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {bom.map((b, i) => (
                <tr key={i} data-testid={`bom-row-${i}`}>
                  <td className="px-2 py-1.5">
                    <Input value={b.description || ""} className="h-8 text-xs"
                      onChange={(e) => setBom((x) => x.map((y, j) => (j === i ? { ...y, description: e.target.value } : y)))} />
                  </td>
                  <td className="px-2 py-1.5 w-56">
                    <select value={b.price_book_id || ""} onChange={(e) => priceFromBook(i, e.target.value)}
                      className="h-8 w-full text-xs border border-slate-300 rounded-md px-1.5"
                      data-testid={`bom-pricebook-${i}`}>
                      <option value="">— pick —</option>
                      {priceRows.map((p) => (
                        <option key={p.id} value={p.id}>{p.category}: {p.description.slice(0, 44)}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-2 py-1.5 w-20">
                    <Input type="number" value={b.qty ?? 1} className="h-8 text-xs text-right"
                      onChange={(e) => setBom((x) => x.map((y, j) => (j === i ? { ...y, qty: e.target.value } : y)))} />
                  </td>
                  <td className="px-2 py-1.5 w-20">
                    <Input value={b.uom || "Each"} className="h-8 text-xs"
                      onChange={(e) => setBom((x) => x.map((y, j) => (j === i ? { ...y, uom: e.target.value } : y)))} />
                  </td>
                  <td className="px-2 py-1.5 w-28">
                    <Input type="number" value={b.unit_price ?? 0} className="h-8 text-xs text-right"
                      onChange={(e) => setBom((x) => x.map((y, j) => (j === i ? { ...y, unit_price: e.target.value } : y)))} />
                  </td>
                  <td className="px-2 py-1.5 w-28 text-right font-bold text-slate-900">
                    ${((Number(b.qty) || 0) * (Number(b.unit_price) || 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                </tr>
              ))}
              {!bom.length && (
                <tr><td colSpan={6} className="px-2 py-4 text-slate-400 text-sm">
                  No BOM lines yet — AskIsaac suggests them from the options once the brief is ready.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-4 mt-3 text-sm">
          <span className="text-slate-500">Cost basis <strong className="text-slate-900">${bomTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong></span>
          <span className="text-slate-500">Markup <strong className="text-slate-900">{markup}%</strong></span>
          <span className="text-slate-500">Sell <strong className="text-emerald-700 text-base">${sell.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong></span>
        </div>
      </Section>

      <BidOwnerDecisions noteId={note.id} token={token} onApplied={reload} />

      <BidScopeTemplates noteId={note.id} token={token} note={doc} onApplied={reload} />

      <StandardsCompass linkType="bid_note" linkId={note.id} token={token}
        label={doc?.title || doc?.customer || "Bid Desk record"} />

      <SiteSurveyPanel linkType="bid_record" linkId={note.id} token={token}
        label={doc?.title || doc?.customer || "this opportunity"} />

      <BidAwardPanel record={doc} token={token} onChanged={reload} />

      <BidNotesProposal noteId={note.id} token={token} />

      <Section icon={NotebookPen} title="Thread" hint="Append the next email and the whole file is re-read"
        testid="notes-thread">
        <div className="space-y-2 mb-3">
          {(doc.thread || []).map((m) => (
            <div key={m.id} className="rounded border border-slate-200 bg-slate-50/60 p-2.5">
              <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">
                {m.label} · {m.at ? new Date(m.at).toLocaleString() : ""}
              </p>
              <p className="text-[12px] text-slate-700 whitespace-pre-wrap line-clamp-6">{m.text}</p>
            </div>
          ))}
        </div>
        <Textarea value={followUp} onChange={(e) => setFollowUp(e.target.value)} rows={4}
          placeholder="Paste the customer's reply here…" className="text-xs" data-testid="notes-followup-text" />
        <div className="flex justify-end mt-2">
          <Button size="sm" onClick={addFollowUp} disabled={busy || !followUp.trim()}
            className="bg-indigo-600 hover:bg-indigo-700" data-testid="notes-followup-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add to thread &amp; re-read
          </Button>
        </div>
      </Section>

      <EquipmentPriceBook open={showBook} onClose={() => setShowBook(false)} token={token} />
    </div>
  );
}
